import clsx from "clsx";
import { BirthDateInput } from "@/components/guests/BirthDateInput";
import { BIRTH_DATE_COPY_DE } from "@shared/birthDateCopy";
import { GUEST_TYPES } from "@/lib/guestType";
import { PlaceBanFields } from "@/components/guests/PlaceBanFields";
import type { GuestCreatePayload } from "@/types/camping";

const inputClass = "eh-input";

export const EMPTY_GUEST_FORM: GuestCreatePayload = {
  guest_type: "tourist",
  title: "",
  first_name: "",
  middle_name: "",
  last_name: "",
  company_name: "",
  vat_id: "",
  leitweg_id: "",
  date_of_birth: "",
  nationality: "",
  email: "",
  phone: "",
  street: "",
  house_number: "",
  address_line_2: "",
  postal_code: "",
  city: "",
  state_region: "",
  country_code: "DE",
  notes: "",
};

export interface GuestDetailsFormProps {
  guest: GuestCreatePayload;
  onChange: (guest: GuestCreatePayload) => void;
  disabled?: boolean;
  autoFocusFirst?: boolean;
}

export function GuestDetailsForm({
  guest,
  onChange,
  disabled,
  autoFocusFirst,
}: GuestDetailsFormProps) {
  return (
    <div className="space-y-4">
      <fieldset className="space-y-2" disabled={disabled}>
        <legend className="text-sm font-medium text-label-primary">Gästetyp</legend>
        <div className="grid gap-2 sm:grid-cols-2">
          {GUEST_TYPES.map((type) => (
            <label
              key={type.value}
              className={clsx(
                "flex cursor-pointer flex-col gap-0.5 rounded-lg border p-3 text-sm transition-colors",
                guest.guest_type === type.value
                  ? "border-accent bg-accent/10"
                  : "border-separator hover:border-label-tertiary",
                disabled && "cursor-not-allowed opacity-60",
              )}
            >
              <span className="flex items-center gap-2 font-medium">
                <input
                  type="radio"
                  name="guest_type"
                  value={type.value}
                  checked={guest.guest_type === type.value}
                  onChange={() => onChange({ ...guest, guest_type: type.value })}
                  disabled={disabled}
                  className="shrink-0"
                />
                {type.label}
              </span>
              <span className="text-label-secondary">{type.description}</span>
            </label>
          ))}
        </div>
      </fieldset>
      <PlaceBanFields guest={guest} onChange={onChange} disabled={disabled} />
      <div className="grid gap-3 sm:grid-cols-2">
      <label className="text-sm">
        <span className="mb-1 block text-label-secondary">Vorname *</span>
        <input
          className={inputClass}
          value={guest.first_name}
          onChange={(e) => onChange({ ...guest, first_name: e.target.value })}
          required
          disabled={disabled}
          autoFocus={autoFocusFirst && !disabled}
        />
      </label>
      <label className="text-sm">
        <span className="mb-1 block text-label-secondary">Nachname *</span>
        <input
          className={inputClass}
          value={guest.last_name}
          onChange={(e) => onChange({ ...guest, last_name: e.target.value })}
          required
          disabled={disabled}
        />
      </label>
      <label className="text-sm sm:col-span-2">
        <span className="mb-1 block text-label-secondary">Firmenname</span>
        <input
          className={inputClass}
          value={guest.company_name}
          onChange={(e) => onChange({ ...guest, company_name: e.target.value })}
          disabled={disabled}
        />
      </label>
      <label className="text-sm">
        <span className="mb-1 block text-label-secondary">Umsatzsteuer-ID</span>
        <input
          className={inputClass}
          value={guest.vat_id}
          onChange={(e) => onChange({ ...guest, vat_id: e.target.value })}
          disabled={disabled}
        />
      </label>
      <label className="text-sm">
        <span className="mb-1 block text-label-secondary">Leitweg-ID (X-Rechnung)</span>
        <input
          className={inputClass}
          value={guest.leitweg_id}
          onChange={(e) => onChange({ ...guest, leitweg_id: e.target.value })}
          disabled={disabled}
          placeholder="nur für öffentliche Auftraggeber"
        />
      </label>
      <div className="text-sm sm:col-span-2">
        <BirthDateInput
          variant="admin"
          legend="Geburtsdatum"
          value={guest.date_of_birth}
          copy={BIRTH_DATE_COPY_DE}
          locale="de"
          disabled={disabled}
          onChange={(date_of_birth) => onChange({ ...guest, date_of_birth })}
        />
      </div>
      <label className="text-sm">
        <span className="mb-1 block text-label-secondary">Nationalität (ISO)</span>
        <input
          className={inputClass}
          value={guest.nationality}
          onChange={(e) =>
            onChange({ ...guest, nationality: e.target.value.toUpperCase().slice(0, 2) })
          }
          maxLength={2}
          placeholder="DE"
          disabled={disabled}
        />
      </label>
      <label className="text-sm">
        <span className="mb-1 block text-label-secondary">E-Mail</span>
        <input
          type="email"
          className={inputClass}
          value={guest.email}
          onChange={(e) => onChange({ ...guest, email: e.target.value })}
          disabled={disabled}
        />
      </label>
      <label className="text-sm">
        <span className="mb-1 block text-label-secondary">Telefon</span>
        <input
          type="tel"
          className={inputClass}
          value={guest.phone}
          onChange={(e) => onChange({ ...guest, phone: e.target.value })}
          disabled={disabled}
        />
      </label>
      <label className="text-sm sm:col-span-2">
        <span className="mb-1 block text-label-secondary">Straße</span>
        <input
          className={inputClass}
          value={guest.street}
          onChange={(e) => onChange({ ...guest, street: e.target.value })}
          disabled={disabled}
        />
      </label>
      <label className="text-sm">
        <span className="mb-1 block text-label-secondary">PLZ</span>
        <input
          className={inputClass}
          value={guest.postal_code}
          onChange={(e) => onChange({ ...guest, postal_code: e.target.value })}
          disabled={disabled}
        />
      </label>
      <label className="text-sm">
        <span className="mb-1 block text-label-secondary">Ort</span>
        <input
          className={inputClass}
          value={guest.city}
          onChange={(e) => onChange({ ...guest, city: e.target.value })}
          disabled={disabled}
        />
      </label>
      <label className="text-sm">
        <span className="mb-1 block text-label-secondary">Land (ISO)</span>
        <input
          className={inputClass}
          value={guest.country_code}
          onChange={(e) =>
            onChange({ ...guest, country_code: e.target.value.toUpperCase().slice(0, 2) })
          }
          maxLength={2}
          placeholder="DE"
          disabled={disabled}
        />
      </label>
      <label className="text-sm sm:col-span-2">
        <span className="mb-1 block text-label-secondary">Notizen</span>
        <textarea
          className={clsx(inputClass, "min-h-[4rem] resize-y")}
          value={guest.notes}
          onChange={(e) => onChange({ ...guest, notes: e.target.value })}
          rows={2}
          disabled={disabled}
        />
      </label>
      </div>
    </div>
  );
}

export function guestCreatePayloadToApi(form: GuestCreatePayload) {
  return {
    guest_type: form.guest_type,
    first_name: form.first_name.trim(),
    last_name: form.last_name.trim(),
    company_name: form.company_name.trim() || null,
    vat_id: form.vat_id.trim() || null,
    leitweg_id: form.leitweg_id.trim() || null,
    email: form.email.trim() || null,
    phone: form.phone.trim() || null,
    street: form.street.trim() || null,
    postal_code: form.postal_code.trim() || null,
    city: form.city.trim() || null,
    country_code: form.country_code.trim() || "DE",
    date_of_birth: form.date_of_birth.trim() || null,
    notes: form.notes.trim() || null,
    is_place_banned: Boolean(form.is_place_banned),
    place_ban_reason:
      form.is_place_banned && form.place_ban_reason?.trim()
        ? form.place_ban_reason.trim()
        : null,
  };
}
